import type {RegionService} from '../../../services/region.service';
import type {StorageService} from '../../../services/storage.service';
import type {SupabaseAuthService} from '../../../services/supabase-auth.service';
import {environment} from '../../../../environments/environment';
import {generateCodeChallenge, generateCodeVerifier, generateState} from '../../../services/oauth-pkce.util';
import {
  accountOAuthEnvKey,
  isDevAuthRegion,
  type AccountOAuthConfig,
} from '../../../supabase.env';

/** sessionStorage key for the PKCE code_verifier + state pair, set by the login page. */
export const OAUTH_PKCE_STORAGE_KEY = 'mp-web-installer-portal-oauth-pkce';

/**
 * After an OAuth failure, skip auto-starting SSO on `/account/login`
 * so the user sees a retry CTA instead of looping.
 */
export const SKIP_AUTO_SSO_KEY = 'mp-web-installer-portal-skip-auto-sso';

export function markSkipAutoSso(): void {
  try {
    sessionStorage.setItem(SKIP_AUTO_SSO_KEY, '1');
  } catch {
    // ignore quota / private-mode failures
  }
}

export function consumeSkipAutoSso(): boolean {
  try {
    const skip = sessionStorage.getItem(SKIP_AUTO_SSO_KEY) === '1';
    sessionStorage.removeItem(SKIP_AUTO_SSO_KEY);
    return skip;
  } catch {
    return false;
  }
}

export interface OAuthPkceState {
  codeVerifier: string;
  state: string;
}

/** Canonical Redirect URI used when starting OAuth (must be allowlisted in Supabase). */
export function buildOAuthCallbackUrl(origin = window.location.origin): string {
  return `${origin}/auth/oauth-callback`;
}

/** mp-auth origin + OAuth client for the Auth project implied by `regionCode`. */
export function resolveAccountOAuthConfig(regionCode: string): AccountOAuthConfig {
  return environment.accountOAuth[accountOAuthEnvKey(regionCode)];
}

export function buildAccountAppUrl(path: string, regionCode: string): string {
  const base = resolveAccountOAuthConfig(regionCode).accountAppUrl.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

/**
 * Local builds default to DEV/LOCAL Auth so SSO hits local/dev mp-auth.
 * Respect an explicit US/EU/CN selection (region switcher → re-login against prod Auth).
 */
export async function ensureDevAuthRegion(storageService: StorageService, regionService: RegionService): Promise<void> {
  const currentRegion = (await storageService.lfGet('region')) as string | null;
  if (currentRegion === 'US' || currentRegion === 'EU' || currentRegion === 'CN') {
    return;
  }
  if (
    (isDevAuthRegion(currentRegion ?? '') || currentRegion === 'LOCAL_PROD')
    && (await regionService.isDebugMode())
  ) {
    return;
  }
  await storageService.lfSet('debugMode', true);
  await regionService.setRegion('LOCAL');
}

/**
 * Build the Supabase authorize URL and persist PKCE locally.
 * Used by the login page and by logout → mp-auth continue (skip bouncing back through login).
 */
export async function prepareAccountAppOAuth(deps: {
  storageService: StorageService;
  regionService: RegionService;
  supabaseAuthService: SupabaseAuthService;
}): Promise<string> {
  const {storageService, regionService, supabaseAuthService} = deps;
  if (!environment.production) {
    await ensureDevAuthRegion(storageService, regionService);
  } else {
    const region = (await regionService.getRegionCode()) || 'US';
    // Prod builds may enter DEV via debugMode; keep that Auth pairing.
    if (!isDevAuthRegion(region)) {
      await regionService.setRegion(region);
    }
  }
  supabaseAuthService.resetClient();

  const regionCode = await regionService.getRegionCode();
  const oauth = resolveAccountOAuthConfig(regionCode);
  const authEnv = await supabaseAuthService.getAuthEnv();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();
  await storageService.lfSet(OAUTH_PKCE_STORAGE_KEY, {codeVerifier, state});

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: oauth.oauthClientId,
    redirect_uri: buildOAuthCallbackUrl(),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    scope: 'openid email',
  });
  return `${authEnv.url}/auth/v1/oauth/authorize?${params.toString()}`;
}

/** Start Authorization Code + PKCE against Supabase OAuth, with mp-auth as the IdP consent UI. */
export async function startAccountAppOAuth(deps: {
  storageService: StorageService;
  regionService: RegionService;
  supabaseAuthService: SupabaseAuthService;
}): Promise<void> {
  window.location.href = await prepareAccountAppOAuth(deps);
}
